import { useMemo } from 'react';
import { AssetClass } from '@monorepo/types';
import { getWidgetStrategy } from '../managers/widget-strategies';
import {
  WidgetDataDependencies,
  WidgetManagerResult,
} from '../types/widget-manager.types';

interface UseWidgetManagerProps {
  assetType: AssetClass | null | undefined;
  widgetData: WidgetDataDependencies;
}

/**
 * Widget manager hook
 * Dynamically configures widgets based on account asset type
 */
export const useWidgetManager = ({
  assetType,
  widgetData,
}: UseWidgetManagerProps): WidgetManagerResult => {
  const result = useMemo(() => {
    // Handle null/undefined asset type
    if (!assetType) {
      return {
        widgets: [],
        isEmpty: true,
        hasWidgets: false,
        error: null,
      };
    }

    // Get strategy for asset type
    const strategy = getWidgetStrategy(assetType);

    if (!strategy) {
      return {
        widgets: [],
        isEmpty: true,
        hasWidgets: false,
        error: `No strategy found for asset type: ${assetType}`,
      };
    }

    try {
      // Get widget configs from strategy
      const widgets = strategy.getWidgetConfigs(widgetData);

      // Validate widget configs
      const validWidgets = widgets.filter((config) => {
        if (!config || !config.type) {
          return false;
        }
        if (config.enabled === false) {
          return false;
        }
        return true;
      });

      return {
        widgets: validWidgets,
        isEmpty: validWidgets.length === 0,
        hasWidgets: validWidgets.length > 0,
        error: null,
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      return {
        widgets: [],
        isEmpty: true,
        hasWidgets: false,
        error: `Error getting widget configs: ${errorMessage}`,
      };
    }
  }, [assetType, widgetData]);

  return result;
};
